// Lightweight local fuzzy + alias/synonym menu search (no external AI).
import { localize, localizeList } from "@/lib/format";
import { getMeta } from "@/data/productMeta";
import { LANGUAGES } from "@/i18n/translations";

// Unicode-safe normalization shared by queries and product text:
// - Turkish dotted/dotless i fold to "i" ("İçecek" ~ "icecek")
// - combining marks are stripped after NFD, which folds Latin diacritics
//   ("köfte" ~ "kofte", "pâtes" ~ "pates"), Cyrillic й/ё (~ и/е) and Arabic
//   harakat/hamza carriers (أ/إ/آ ~ ا)
// - Arabic ta marbuta/alef maqsura and tatweel are folded
// - every letter/number in ANY script is kept; only punctuation/symbols
//   become separators — Arabic and Cyrillic text is never erased.
export function norm(s = "") {
  return String(s)
    .replace(/İ/g, "i")
    .toLowerCase()
    .replace(/ı/g, "i")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/ـ/g, "")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

// Synonym clusters. Any token matching a cluster also matches its siblings.
// Entries are written naturally and normalized once at load time.
const SYNONYMS = [
  ["burger", "hamburger", "cheeseburger", "cheesburger", "hamburg", "burgr", "hambuger", "kofte", "smash", "бургер", "гамбургер", "чизбургер", "برجر", "برغر", "همبرغر", "هامبرغر"],
  ["pizza", "pizzas", "margherita", "pepperoni", "peperoni", "пицца", "بيتزا"],
  ["patates", "fries", "frenchfries", "potato", "potatoes", "wedges", "cips", "kizartma", "frites", "картофель", "картошка", "фри", "بطاطس", "بطاطا"],
  ["tavuk", "chicken", "citir", "crispy", "poulet", "hähnchen", "курица", "куриный", "دجاج"],
  ["kahve", "coffee", "latte", "espresso", "cappuccino", "café", "kaffee", "кофе", "латте", "قهوة", "لاتيه"],
  ["kola", "cola", "coke", "gazoz", "soda", "softdrink", "газировка", "صودا"],
  ["icecek", "drink", "beverage", "limonata", "lemonade", "ayran", "water", "boisson", "getränk", "напиток", "напитки", "مشروب", "مشروبات"],
  ["tatli", "dessert", "sweet", "baklava", "sufle", "souffle", "cheesecake", "sutlac", "cake", "nachtisch", "десерт", "сладкое", "حلويات", "حلوى"],
  ["makarna", "pasta", "spaghetti", "penne", "fettuccine", "carbonara", "pesto", "arrabiata", "alfredo", "pâtes", "паста", "макароны", "معكرونة", "مكرونة"],
  ["salata", "salad", "sezar", "caesar", "kinoa", "quinoa", "bowl", "salade", "salat", "салат", "سلطة"],
  ["kahvalti", "breakfast", "menemen", "simit", "serpme", "pankek", "pancake", "yumurta", "egg", "frühstück", "завтрак", "فطور", "إفطار"],
  ["vejetaryen", "vegetarian", "vegan", "etsiz", "végétarien", "vegetarisch", "вегетарианский", "نباتي"],
  ["acili", "spicy", "hot", "aci", "épicé", "scharf", "острый", "حار"],
].map((cluster) => [...new Set(cluster.map(norm).filter(Boolean))]);

function expandToken(tok) {
  const set = new Set([tok]);
  SYNONYMS.forEach((cluster) => {
    const hit = cluster.some(
      (w) => w === tok || (tok.length >= 4 && w.length >= 3 && (w.includes(tok) || tok.includes(w)))
    );
    if (hit) cluster.forEach((w) => set.add(w));
  });
  return [...set];
}

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

// Build a normalized haystack of searchable terms for a product. Names in every
// supported language are included so a guest can find a dish by any name.
function haystack(product, categoryName, lang) {
  const meta = getMeta(product.id);
  const parts = [
    ...LANGUAGES.map((l) => localize(product.name, l.code)),
    localize(product.description, lang),
    ...localizeList(product.ingredients, lang),
    ...localizeList(product.ingredients, "tr"),
    categoryName,
    ...(meta.aliases || []),
    ...(meta.tags || []),
  ];
  return norm(parts.join(" "));
}

// Returns { results: Product[], suggestions: Product[] }.
// results = strong matches. suggestions = close/fuzzy "did you mean" items.
export function searchProducts(query, products, categoryNameFor, lang) {
  const raw = String(query ?? "");
  if (!raw.trim()) return { results: products, suggestions: [] };
  const q = norm(raw);
  // A non-empty query that normalizes to nothing (only symbols/emoji) matches
  // nothing — it must never fall through to "show the whole menu".
  if (!q) return { results: [], suggestions: [] };
  const tokens = q.split(" ").filter(Boolean);

  const scored = products.map((p) => {
    const hay = haystack(p, categoryNameFor(p), lang);
    const words = hay.split(" ");
    let score = 0;

    tokens.forEach((tok) => {
      const variants = expandToken(tok);
      let best = 0;
      variants.forEach((v) => {
        if (hay.includes(v)) best = Math.max(best, v === tok ? 10 : 7);
      });
      if (best === 0) {
        // typo tolerance against individual words
        for (const w of words) {
          if (!w) continue;
          const d = levenshtein(tok, w);
          const tol = tok.length <= 4 ? 1 : 2;
          if (d <= tol) { best = Math.max(best, 4); break; }
          if (w.startsWith(tok) && tok.length >= 3) { best = Math.max(best, 6); }
        }
      }
      score += best;
    });

    return { product: p, score };
  });

  const strong = scored.filter((s) => s.score >= tokens.length * 6).sort((a, b) => b.score - a.score);
  const loose = scored.filter((s) => s.score > 0 && s.score < tokens.length * 6).sort((a, b) => b.score - a.score);

  if (strong.length) {
    return { results: strong.map((s) => s.product), suggestions: [] };
  }
  // No strong hits but close matches exist -> "did you mean"
  return { results: [], suggestions: loose.slice(0, 6).map((s) => s.product) };
}
